import { useState } from 'react';
import { Readability } from '@mozilla/readability';
import DOMPurify from 'dompurify';
import { Sun, Moon, Type as TypeIcon, BookOpen, Copy, Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Slider } from '@/components/ui/Slider';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard, download } from '@/lib/utils';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

const SAMPLE_HTML = `<!doctype html><html><head><title>Demo article</title></head><body>
<header><nav>menu</nav></header>
<main><article>
<h1>The case for plain language</h1>
<p>Plain language is more inclusive. Short sentences and common words help everyone — not just newcomers — read faster and remember more.</p>
<p>When teams default to clear writing, customer-support tickets drop, onboarding speeds up, and decisions move faster.</p>
<h2>Three rules</h2>
<ol><li>Lead with the action.</li><li>Cut adverbs.</li><li>Replace jargon with plain words.</li></ol>
</article></main>
<footer>copyright</footer>
</body></html>`;

interface Article {
  title?: string | null;
  byline?: string | null;
  content?: string | null;
  textContent?: string | null;
  excerpt?: string | null;
  length?: number;
  siteName?: string | null;
}

function parse(html: string): Article | null {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const article = new Readability(doc).parse();
    return article as Article | null;
  } catch {
    return null;
  }
}

export function ReadingMode() {
  const [mode, setMode] = useState<'html' | 'url'>('html');
  const [html, setHtml] = useState(SAMPLE_HTML);
  const [url, setUrl] = useState('');
  const [article, setArticle] = useState<Article | null>(null);
  const [working, setWorking] = useState(false);
  const [theme, setTheme] = useState<'light' | 'sepia' | 'dark'>('light');
  const [size, setSize] = useState(17);

  function parseHtml() {
    const a = parse(html);
    if (a) setArticle(a);
    else toast.error('No readable content found');
  }

  async function fetchUrl() {
    if (!url.trim()) return;
    setWorking(true);
    try {
      // Try direct fetch first; many sites block CORS, fall back to public proxy.
      let body = '';
      try {
        const r = await fetch(url);
        body = await r.text();
      } catch {
        const r = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`);
        body = await r.text();
      }
      const a = parse(body);
      if (a) {
        setArticle(a);
      } else {
        toast.error('Could not extract readable content');
      }
    } catch {
      toast.error('Fetch failed — check the URL or use HTML mode');
    } finally {
      setWorking(false);
    }
  }

  const html2 = article?.content ? DOMPurify.sanitize(article.content) : '';
  const themeStyles =
    theme === 'sepia'
      ? { background: '#f6f0e1', color: '#3a2f1c' }
      : theme === 'dark'
      ? { background: '#0f0f10', color: '#e7e5e4' }
      : {};

  return (
    <div>
      <ToolHeader
        toolId="reading-mode"
        title="Reading mode"
        description="Strip clutter from any web page or HTML and render the article in a calm, readable view."
        badges={article ? <Badge variant="success">{article.length} chars</Badge> : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Tabs value={mode} onValueChange={(v) => setMode(v as 'html' | 'url')}>
            <TabsList className="w-full">
              <TabsTrigger value="html" className="flex-1">HTML</TabsTrigger>
              <TabsTrigger value="url" className="flex-1">URL</TabsTrigger>
            </TabsList>
            <TabsContent value="html">
              <Field label="HTML source" className="mt-3">
                <Textarea
                  mono
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  className="min-h-[180px] text-[12px]"
                />
              </Field>
              <Button variant="primary" size="sm" className="w-full mt-3" onClick={parseHtml}>
                <BookOpen size={11} />
                Render
              </Button>
            </TabsContent>
            <TabsContent value="url">
              <Field label="Article URL" className="mt-3">
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/article"
                />
              </Field>
              <Button
                variant="primary"
                size="sm"
                className="w-full mt-3"
                loading={working}
                onClick={fetchUrl}
              >
                <BookOpen size={11} />
                Fetch & render
              </Button>
              <div className="text-2xs text-fg-subtle mt-2 leading-relaxed">
                Some sites block direct fetch — we fall back to a CORS proxy.
              </div>
            </TabsContent>
          </Tabs>
          {article && (
            <div className="border-t border-border pt-3 space-y-3">
              <Field label={`Theme`}>
                <div className="grid grid-cols-3 gap-1">
                  {([
                    { id: 'light', icon: Sun, label: 'Light' },
                    { id: 'sepia', icon: BookOpen, label: 'Sepia' },
                    { id: 'dark', icon: Moon, label: 'Dark' },
                  ] as const).map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className={cn(
                        'flex flex-col items-center gap-1 py-2 rounded-md text-2xs transition',
                        theme === t.id
                          ? 'bg-accent/10 text-accent border border-accent/30'
                          : 'border border-border hover:border-border-strong',
                      )}
                    >
                      <t.icon size={12} />
                      {t.label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Font size" hint={`${size}px`}>
                <Slider value={size} onChange={setSize} min={14} max={22} showValue={false} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    const ok = await copyToClipboard(article.textContent ?? '');
                    if (ok) toast.success('Text copied');
                  }}
                >
                  <Copy size={11} />
                  Text
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => download(article.textContent ?? '', 'article.txt', 'text/plain')}
                >
                  <Download size={11} />
                  TXT
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            Article view
          </div>
          {!article ? (
            <div className="p-12 text-center text-sm text-fg-subtle">
              Paste HTML or a URL to render a clean reading view.
            </div>
          ) : (
            <div
              className="px-8 py-10 overflow-auto max-h-[700px] transition-colors"
              style={{ ...themeStyles, fontSize: `${size}px` }}
            >
              <article className="prose-uos max-w-2xl mx-auto" style={{ fontSize: `${size}px` }}>
                <h1 style={{ fontSize: `${size * 1.6}px` }}>{article.title}</h1>
                {article.byline && (
                  <div className="text-sm opacity-70 mb-4">{article.byline}</div>
                )}
                <div
                  className="leading-[1.7]"
                  dangerouslySetInnerHTML={{ __html: html2 }}
                />
              </article>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default ReadingMode;
